import { ButtonLink, ButtonSize } from '@farmasiet/frontendcore-reactcomponents';
import * as React from 'react';

import { getTranslation, keys } from '../../../../../../Resources/Translations';
import { OrderHistoryInfoViewModel } from '../../../../../Blocks/OrderHistoryInfoBlock/OrderHistoryInfoViewModel.csharp';
import { Loader } from '../../../../../Components/Loader/Loader';
import { getFor } from '../../../../../ReactComponents/API';
import { RenderHTML } from '../../../../../ReactComponents/RenderHTML';
import { smoothScrollTo } from '../../../../../ReactComponents/SmoothScrollTo';
import { OrderResultStatus } from '../../Services/Models/OrderResultStatus.csharp';
import { OrderListItemViewModel } from '../../ViewModel/OrderListItemViewModel.csharp';
import { OrderListViewModel } from '../../ViewModel/OrderListViewModel.csharp';
import { OrderListEmptyState } from './OrderListEmptyState';
import { OrderListItem } from './OrderListItem';

interface OrderListProps {
    orderUrl: string;
    orderDetailsUrl: string;
    infoBlock: OrderHistoryInfoViewModel;
    rxOrderHistoryUrl: string;
}

export const OrderList = ({ orderUrl, orderDetailsUrl, infoBlock, rxOrderHistoryUrl }: OrderListProps) => {
    const pageSize = 15;
    const [orderList, setOrderList] = React.useState<OrderListItemViewModel[]>();
    const [hasNextPage, setHasNextPage] = React.useState(false);
    const [isInitializing, setIsInitializing] = React.useState(true);
    const [isLoading, setIsLoading] = React.useState(false);
    const [isError, setIsError] = React.useState(false);
    const [currentPage, setCurrentPage] = React.useState(1);
    const [shouldScroll, setShouldScroll] = React.useState(false);
    const [resultStatus, setResultStatus] = React.useState<OrderResultStatus>();

    const containerRef = React.useRef<HTMLDivElement>(null);

    const getOrders = (page: number) => {
        setIsLoading(true);
        setIsError(false);
        const skip = (page - 1) * pageSize;

        getFor<OrderListViewModel>(`${orderUrl}?includeInProgressOrders=${skip === 0}&skip=${skip}&take=${pageSize}`)
            .then((result: OrderListViewModel) => {
                containerRef.current && shouldScroll && smoothScrollTo(containerRef.current.offsetTop - 100);
                setShouldScroll(true);
                setResultStatus(result.resultStatus);

                if (result.resultStatus === OrderResultStatus.Ok) {
                    setOrderList(result.orders);
                    setHasNextPage(result.hasNextPage);
                    setCurrentPage(page);
                } else {
                    setIsError(true);
                }
                setIsInitializing(false);
                setIsLoading(false);
            })
            .catch(() => {
                setIsLoading(false);
                setIsInitializing(false);
                setIsError(true);
            });
    };

    React.useEffect(() => {
        getOrders(1);
    }, []);

    const goToFirst = () => {
        getOrders(1);
    };

    const goToPrev = () => {
        getOrders(currentPage - 1);
    };

    const goToNext = () => {
        getOrders(currentPage + 1);
    };

    return (
        <>
            {!isInitializing && (
                <div className="OrderHistory__Content">
                    {rxOrderHistoryUrl && (
                        <ButtonLink size={ButtonSize.Large} href={rxOrderHistoryUrl} target="_blank" rel="noopener noreferrer">
                            {getTranslation(keys['userProfile.orderHistory.prescriptionHistory'])}
                        </ButtonLink>
                    )}

                    {infoBlock.isActive && infoBlock.message && <RenderHTML html={infoBlock.message} className="OrderHistory__InfoBlock" />}
                </div>
            )}
            <div className="OrderList OrderList__Section" ref={containerRef}>
                {isLoading && <Loader isLoading={true} className="OrderList__Loader" />}

                {isError && <OrderListEmptyState status={resultStatus} />}

                {orderList && (
                    <>
                        {orderList.length === 0 && <OrderListEmptyState status={resultStatus} />}

                        {orderList.length > 0 && (
                            <ul className="OrderList__List">
                                {orderList.map(
                                    (order) =>
                                        order.orderNumber && (
                                            <li key={order.orderId} className="OrderList__OrderItem">
                                                <OrderListItem order={order} orderDetailsUrl={orderDetailsUrl} />
                                            </li>
                                        )
                                )}
                            </ul>
                        )}

                        {orderList.length > 0 && (
                            <div className="OrderList__Pagination">
                                {currentPage > 2 && (
                                    <button className="OrderList__PaginationButton OrderList__PaginationButton--First" onClick={goToFirst}>
                                        <span className="visuallyHidden">{getTranslation(keys['userProfile.orderHistory.paging.firstPage'])}</span>
                                    </button>
                                )}
                                {currentPage > 1 && (
                                    <button className="OrderList__PaginationButton OrderList__PaginationButton--Prev" onClick={goToPrev}>
                                        <span className="visuallyHidden">{getTranslation(keys['userProfile.orderHistory.paging.previousPage'])}</span>
                                    </button>
                                )}
                                <span className="OrderList__PaginationPage">{currentPage}</span>
                                {hasNextPage && (
                                    <button className="OrderList__PaginationButton OrderList__PaginationButton--Next" onClick={goToNext}>
                                        <span className="visuallyHidden">{getTranslation(keys['userProfile.orderHistory.paging.nextPage'])}</span>
                                    </button>
                                )}
                            </div>
                        )}
                    </>
                )}
            </div>
        </>
    );
};
